/* eslint-disable no-prototype-builtins */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo, useCallback } from "react";

import { IconSuccess } from "~misc/icons";
import { db } from "~/db/db";
import useOnlineStatus from "~hooks/useOnlineStatus";
import useMapaMina from "~fetchs/useMapaMina";
import originControl from "~services/utils/originControl";

import useMapaMinaClienteFechado from "~fetchs/useMapaMinaClienteFechado";
import { checkTodaySearchs, formatDateToCustomString, toFormatSearchs } from "~pages/PriceSearchV2/utils/searchHelpers";
import { getOrderedDatesArray, getOrganizedDates } from "~pages/PriceSearchV2/utils/initializerHelpers";

const usePriceSearchData = ({ clienteId, setDeletarId, deletarId, origem, filters, pesquisaObjetos }) => {
  const [dataIsNotPresentable, setDataIsNotPresentable] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);

  const [showMessageForm, setShowMessageForm] = useState({
    text: "",
    type: "success",
    icon: IconSuccess,
  });

  const { origens } = originControl();

  const [hook, setHook] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [lastMapaRequest, setLastMapaRequest] = useState([]);
  const [lastSearchDate, setLastSearchDate] = useState(null);
  const [filteredOnce, setFilteredOnce] = useState(0);
  const [forceRefetch, setForceRefetch] = useState(0);
  const [backupData, setBackupData] = useState([]);
  const isOnline = useOnlineStatus();

  const updateHook = useCallback(() => {
    setHook((prev) => prev + 1);
  }, []);

  const refetchMapaMina = () => {
    setForceRefetch((prev) => prev + 1);
    updateHook();
  };

  const resetOrderedIds = useCallback(() => {
    // Limpa o backup para forçar nova ordenação
    setBackupData([]);
    updateHook();
  }, [updateHook]);
  const { mapasMina, searchedCount, loadingMapaRequest } = useMapaMina({
    hook,
    updateHook,
    clienteId,
    filters,
    pesquisaPlayers: pesquisaObjetos?.pesquisaPlayers,
    updateMapaData: (!origem || origem != origens.editar_pesquisa) && isOnline,
    isOnline,
    forceRefetch,
  });

  const filteredData = useMemo(() => {
    let dados = toFormatSearchs({ searchs: mapasMina, pesquisaObjetos });

    if (dados?.length > 0 && backupData.length === 0) {
      setBackupData(dados);
    }

    if (filters) {
      if (filters.produto) {
        dados = dados.filter((item) => item.dadosGerais?.produtoId === filters.produto);
      }
      if (filters.grupoProduto) {
        dados = dados.filter((item) => item.dadosGerais?.grupoProdutoId === filters.grupoProduto);
      }

      if (filters.periodo) {
        const now = new Date();
        let startDate;

        switch (filters.periodo) {
          case "hoje":
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case "seteDias":
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case "quinzeDias":
            startDate = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
            break;
          case "trintaDias":
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = "";
        }

        if (startDate) {
          dados = dados.filter((item) => {
            const itemDate = new Date(item.ultimaAtualizacaoEN || item.updatedAtEN || item.createdAtEN);
            return itemDate >= startDate;
          });
        }
      }
    }

    if (backupData.length > 0) {
      const currentOrder = filters?.order || "recentes";

      const sortedBackup = backupData.toSorted((a, b) => {
        const dateA = new Date(a.ultimaAtualizacaoEN);
        const dateB = new Date(b.ultimaAtualizacaoEN);

        if (currentOrder === "asc") {
          return dateA - dateB;
        } else {
          return dateB - dateA;
        }
      });

      const idMap = new Map(dados.map((item) => [item.id, item]));
      const orderedData = [];

      for (const item of sortedBackup) {
        const currentItem = idMap.get(item.id);
        if (currentItem) {
          orderedData.push(currentItem);
        }
      }

      const backupIds = sortedBackup.map((item) => item.id);
      const newItems = dados.filter((item) => !backupIds.includes(item.id));

      if (newItems.length > 0) {
        const sortedNewItems = [...newItems].sort((a, b) => {
          const dateA = new Date(a.ultimaAtualizacaoEN);
          const dateB = new Date(b.ultimaAtualizacaoEN);

          if (currentOrder === "asc") {
            return dateA - dateB;
          } else {
            return dateB - dateA;
          }
        });

        if (currentOrder === "asc") {
          dados = [...orderedData, ...sortedNewItems];
        } else {
          dados = [...sortedNewItems, ...orderedData];
        }
      } else {
        dados = orderedData;
      }
    }

    return dados;
  }, [mapasMina, filters, pesquisaObjetos, backupData]);

  const filteredItemsCount = useMemo(() => {
    return filteredData.filter((item) => item.ativo === true).length;
  }, [filteredData]);

  // Controla o loading baseado nos dados filtrados
  useEffect(() => {
    if (filteredData.length >= 0) {
      setIsLoadingData(false);
    }
  }, [filteredData]);

  const { fechadoConsomeBasico, open } = useMapaMinaClienteFechado({
    clienteId,
    updateData: (!origem || origem != origens.editar_pesquisa) && isOnline,
  });

  useEffect(() => {
    const deleteMapaMina = async () => {
      const { id } = await checkTodaySearchs({
        clienteId: clienteId,
        pesquisaId: deletarId,
        obj: {
          ativo: false,
          deletedAt: formatDateToCustomString(new Date()),
          updated: true,
        },
      });

      const toDeleteId = id != 0 ? id : deletarId;

      setIsLoadingDelete(true);

      await db.mapaDaMina
        .where("id")
        .equals(Number.parseInt(toDeleteId))
        .first()
        .then((mapaMina) => {
          let today = new Date();

          let createdAtDate = new Date(mapaMina.createdAt);
          createdAtDate = createdAtDate.setHours(0, 0, 0, 0);

          const compareToday = today.setHours(0, 0, 0, 0);

          if (createdAtDate == compareToday) {
            db.mapaDaMina.update(mapaMina.id, { deletedAt: formatDateToCustomString(new Date()), ativo: false });
          } else {
            db.mapaDaMina.update(mapaMina.id, {
              ativo: false,
              deletadoEm: formatDateToCustomString(new Date()),
              updated: true,
            });
          }

          setDeletarId(0);
        })
        .finally(() => {
          updateHook();
          setIsLoadingDelete(false);
        });
    };

    if (deletarId !== 0) {
      deleteMapaMina();
    }
  }, [deletarId]);

  const getLastSearchDate = useMemo(() => {
    const { dates } = getOrganizedDates({ data: lastMapaRequest.filter((e) => !e.deletadoEm && !e.notSave) });

    let lastSearchDate = null;

    const keys = Object.keys(dates);

    const ordered = getOrderedDatesArray({ data: [...keys] }).reverse();

    for (const key of ordered) {
      const value = dates[key];

      if (
        value.filter((e) => {
          const hasNumericId = typeof e.id == "number" || e.wasDuplicated;
          const hasClientStatus = e.clienteFechado || e.clienteNaoConsomeBasicos;
          return (hasNumericId || hasClientStatus) && !e.new;
        })?.length > 0
      ) {
        lastSearchDate = key;
        break;
      }
    }

    return lastSearchDate;
  }, [lastMapaRequest]);

  const getTotal = useCallback(async () => {
    if (!clienteId) return;

    let totalItems = await db.mapaDaMina
      .where("clienteId")
      .equals(parseInt(clienteId))
      .and((item) => item.deletedAt === undefined && item.ativo === true)
      .count();
    setTotalItems(totalItems);
  }, [mapasMina]);

  useEffect(() => {
    setLastSearchDate(getLastSearchDate);
  }, [getLastSearchDate]);

  const pesquisaObjectsKeys = useMemo(() => {
    return {
      canaisDistribuicao: Object.keys(
        pesquisaObjetos?.pesquisaCanaisDistribuicao?.pesquisaCanalDistribuicaoObjeto || {}
      ),
      grupoMateriais: Object.keys(pesquisaObjetos?.pesquisaGrupoMateriais?.pesquisaGrupoMaterialObjeto || {}),
      produtos: Object.keys(pesquisaObjetos?.pesquisaProdutos?.pesquisaProdutoObjeto || {}),
      embalagens: Object.keys(pesquisaObjetos?.pesquisaEmbalagens?.pesquisaEmbalagemObjeto || {}),
      players: Object.keys(pesquisaObjetos?.pesquisaPlayers?.pesquisaPlayerObjeto || {}),
      prazos: Object.keys(pesquisaObjetos?.pesquisaPrazos?.pesquisaPrazoObjeto || {}),
      medidas: Object.keys(pesquisaObjetos?.pesquisaMedidas?.pesquisaMedidaObjeto || {}),
      centros: Object.keys(pesquisaObjetos?.pesquisaCentros?.pesquisaCentroOrigemObjeto || {}),
    };
  }, [
    pesquisaObjetos?.pesquisaCanaisDistribuicao?.pesquisaCanalDistribuicaoObjeto,
    pesquisaObjetos?.pesquisaGrupoMateriais?.pesquisaGrupoMaterialObjeto,
    pesquisaObjetos?.pesquisaProdutos?.pesquisaProdutoObjeto,
    pesquisaObjetos?.pesquisaEmbalagens?.pesquisaEmbalagemObjeto,
    pesquisaObjetos?.pesquisaPlayers?.pesquisaPlayerObjeto,
    pesquisaObjetos?.pesquisaPrazos?.pesquisaPrazoObjeto,
    pesquisaObjetos?.pesquisaMedidas?.pesquisaMedidaObjeto,
    pesquisaObjetos?.pesquisaCentros?.pesquisaCentroOrigemObjeto,
  ]);

  useEffect(() => {
    const hasAnyData = Object.values(pesquisaObjectsKeys).some((keys) => keys.length > 0);
    if (hasAnyData) {
      updateHook();
    }
  }, [pesquisaObjectsKeys, updateHook]);

  useEffect(() => {
    getTotal();
  }, [getTotal]);

  useEffect(() => {
    const playersObj = pesquisaObjetos?.pesquisaPlayers?.pesquisaPlayerObjeto;
    const availablePlayers = pesquisaObjetos?.pesquisaPlayers?.availablePlayers;

    setLastMapaRequest(mapasMina);

    if (
      playersObj &&
      typeof playersObj == "object" &&
      availablePlayers &&
      typeof availablePlayers == "object" &&
      ((mapasMina.length === 0 && searchedCount > 0) || mapasMina.length)
    ) {
      setFilteredOnce((prev) => prev + 1);
    }
  }, [mapasMina.length, searchedCount]);

  useEffect(() => {
    if (filteredData?.length === 0 || (fechadoConsomeBasico && typeof fechadoConsomeBasico == "string")) {
      setDataIsNotPresentable(true);
    } else {
      setDataIsNotPresentable(false);
    }
  }, [filteredData, fechadoConsomeBasico]);

  return {
    filteredData,
    isLoadingData,
    isLoadingDelete,
    searchedCount,
    dataIsNotPresentable,
    showMessageForm,
    totalItems,
    lastSearchDate,
    filteredItemsCount,
    filteredOnce,
    updateHook,
    setShowMessageForm,
    refetchMapaMina,
    resetOrderedIds,
    fechadoConsomeBasico,
    loadingMapaRequest,
    open,
    mapasMina,
  };
};

export default usePriceSearchData;
